import appStore from "@/utils/store";
import { translate } from "@/utils/format";
import { PRODUCTS } from "./products";
import type {
    ActivityItem,
    DailyPoint,
    MetricsCacheEntry,
    RangeKey,
    SubscriptionRow,
    UserRow,
} from "./types";

export const METRICS_CACHE_TTL_MS = 60 * 60 * 1000; // 1h
export const METRICS_CACHE_MAX_AGE_MS = 24 * 60 * 60 * 1000; // keep at most 24h

export function metricsCacheKey(
    userId: string,
    referralId: string,
    rangeKey: RangeKey,
) {
    return `partnerMetricsCache:v1:${userId}:${referralId}:${rangeKey}`;
}

export function readMetricsCache(
    userId: string,
    referralId: string,
    rangeKey: RangeKey,
): MetricsCacheEntry | null {
    try {
        const raw = appStore.get(
            metricsCacheKey(userId, referralId, rangeKey),
        );
        if (!isRecord(raw)) return null;

        const fetchedAt = asNumber((raw as any).fetchedAt, 0);
        if (!Number.isFinite(fetchedAt) || fetchedAt <= 0) return null;

        return {
            fetchedAt,
            res: (raw as any).res,
        };
    } catch {
        return null;
    }
}

export function writeMetricsCache(
    userId: string,
    referralId: string,
    rangeKey: RangeKey,
    entry: MetricsCacheEntry,
) {
    try {
        appStore.set(metricsCacheKey(userId, referralId, rangeKey), entry);

        // Evict old/broken cache entries to prevent unbounded growth
        appStore.cleanup((k, v) => {
            if (typeof k !== "string") return false;
            if (!k.startsWith("partnerMetricsCache:v1:")) return false;

            if (!isRecord(v)) return true;
            const fetchedAt = asNumber((v as any).fetchedAt, 0);
            if (!Number.isFinite(fetchedAt) || fetchedAt <= 0) return true;

            return Date.now() - fetchedAt > METRICS_CACHE_MAX_AGE_MS;
        });
    } catch {
        // ignore storage errors
    }
}

export function isCacheFresh(entry: MetricsCacheEntry) {
    return Date.now() - entry.fetchedAt < METRICS_CACHE_TTL_MS;
}

export function daysForRange(r: RangeKey): number {
    if (r === "7d") return 7;
    if (r === "90d") return 90;
    if (r === "all") return 3650; // 10 years
    return 30;
}

export function getClientTimeZone(): string {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
    } catch {
        return "UTC";
    }
}

export function buildMetricsQuery(r: RangeKey) {
    const days = daysForRange(r);
    const to = new Date();
    const from = new Date();
    from.setDate(to.getDate() - days);

    return {
        from: from.toISOString(),
        to: to.toISOString(),
        tz: getClientTimeZone(),
        granularity: "day",
        include: "series,activity,subscriptions,users",
    };
}

export function isRecord(v: unknown): v is Record<string, any> {
    return !!v && typeof v === "object";
}

export function asNumber(v: unknown, fallback = 0): number {
    const n = typeof v === "number" ? v : Number(v);
    return Number.isFinite(n) ? n : fallback;
}

export function asString(v: unknown, fallback = ""): string {
    if (typeof v === "string") return v;
    if (v == null) return fallback;
    return String(v);
}

export function asEpochMs(v: unknown): number {
    if (typeof v === "number") {
        // Heuristic: seconds vs ms
        return v < 1_000_000_000_000 ? v * 1000 : v;
    }
    if (typeof v === "string") {
        const n = Number(v);
        if (Number.isFinite(n)) return asEpochMs(n);
        const t = Date.parse(v);
        return Number.isFinite(t) ? t : 0;
    }
    if (v instanceof Date) return v.getTime();
    return 0;
}

export function pickArray(obj: unknown, keys: string[]): any[] {
    if (!isRecord(obj)) return [];
    for (const k of keys) {
        const v = obj[k];
        if (Array.isArray(v)) return v;
    }
    return [];
}

export function isoDay(d: Date): string {
    const z = new Date(d);
    z.setHours(0, 0, 0, 0);
    return z.toISOString();
}

export function toIsoDay(v: unknown): string {
    if (v instanceof Date) return isoDay(v);
    if (typeof v === "string") {
        const t = Date.parse(v);
        if (Number.isFinite(t)) return isoDay(new Date(t));
        return "";
    }
    const ms = asEpochMs(v);
    return ms ? isoDay(new Date(ms)) : "";
}

export function coerceDailyPoint(v: unknown): DailyPoint | null {
    if (!isRecord(v)) return null;
    const date =
        toIsoDay(v["date"]) ||
        toIsoDay(v["day"]) ||
        toIsoDay(v["ts"]) ||
        toIsoDay(v["time"]);
    if (!date) return null;

    return {
        date,
        installs: asNumber(v["installs"], 0),
        newSubs: asNumber(
            v["newSubs"] ??
                v["new_subs"] ??
                v["newSubscribers"] ??
                v["subscribers"],
            0,
        ),
        cancels: asNumber(
            v["cancels"] ?? v["canceled"] ?? v["cancelled"],
            0,
        ),
        renewals: asNumber(v["renewals"] ?? v["renews"], 0),
    };
}

export function coerceActivityItem(v: unknown, i: number): ActivityItem | null {
    if (!isRecord(v)) return null;
    const rawType = asString(
        v["type"] || v["event"] || v["action"],
        "",
    ).toLowerCase();
    const type: ActivityItem["type"] =
        rawType === "install" || rawType === "installed"
            ? "install"
            : rawType === "newsub" ||
                rawType === "new_sub" ||
                rawType === "subscription" ||
                rawType === "subscription_started" ||
                rawType === "subscriber_started"
              ? "newSub"
              : rawType === "renewal" ||
                  rawType === "renew" ||
                  rawType === "subscription_renewed"
                ? "renewal"
                : rawType === "cancel" ||
                    rawType === "canceled" ||
                    rawType === "cancelled" ||
                    rawType === "subscription_canceled" ||
                    rawType === "subscription_cancelled"
                  ? "cancel"
                  : "install";

    const ts = asEpochMs(
        v["ts"] ?? v["timestamp"] ?? v["createdAt"] ?? v["date"],
    );
    const label = asString(v["label"], "").trim();

    const meta = isRecord(v["meta"])
        ? (v["meta"] as Record<string, any>)
        : null;
    const metaPriceId = asString(
        meta?.["priceId"] ?? meta?.["productId"],
        "",
    );
    const metaProductId = asString(meta?.["productId"], "");
    const product = PRODUCTS.find(
        (p) =>
            (metaPriceId && p.priceId === metaPriceId) ||
            (metaProductId && p.id === metaProductId) ||
            (metaProductId && p.priceId === metaProductId),
    );

    let priceText = "";
    const money = meta?.["amount"];
    if (isRecord(money)) {
        const cents = asNumber(money["amount"], NaN);
        const currency = asString(money["currency"], "");
        if (Number.isFinite(cents) && currency) {
            const amount = (cents / 100).toFixed(2);
            priceText = `${amount} ${currency}`;
        }
    }

    const detailsParts: string[] = [];
    if (product?.name) detailsParts.push(product.name);
    else if (metaPriceId) detailsParts.push(metaPriceId);
    if (priceText) detailsParts.push(priceText);

    const details = detailsParts.join(" · ");
    const fallbackLabel =
        type === "install"
            ? translate(
                  "LANG_PARTNER_ACTIVITY_INSTALL",
                  "Nueva instalación",
              )
            : type === "newSub"
              ? translate(
                    "LANG_PARTNER_ACTIVITY_NEW_SUB",
                    "Nueva suscripción",
                )
              : type === "renewal"
                ? translate("LANG_PARTNER_ACTIVITY_RENEW", "Renovación")
                : translate("LANG_PARTNER_ACTIVITY_CANCEL", "Cancelación");

    return {
        id:
            asString(v["subscriptionId"], "") ||
            asString(v["id"], "") ||
            `${type}-${ts || 0}-${i}`,
        type,
        label: label || fallbackLabel,
        ts: ts || Date.now(),
        details,
    };
}

export function maskEmailValue(email: string): string {
    const e = (email || "").trim();
    const at = e.indexOf("@");
    if (at <= 1) return e || "—";
    const name = e.slice(0, at);
    const domain = e.slice(at + 1);
    return `${name.slice(0, 2)}***@${domain}`;
}

export function coerceSubscriptionRow(
    v: unknown,
    i: number,
): SubscriptionRow | null {
    if (!isRecord(v)) return null;

    const statusRaw = asString(v["status"], "").toLowerCase();
    const status: SubscriptionRow["status"] =
        statusRaw === "active"
            ? "active"
            : statusRaw === "trialing" || statusRaw === "trial"
              ? "trialing"
              : statusRaw === "past_due" || statusRaw === "pastdue"
                ? "past_due"
                : statusRaw === "canceled" || statusRaw === "cancelled"
                  ? "canceled"
                  : "active";

    const planRaw = asString(
        v["plan"] ?? v["interval"] ?? v["billingInterval"],
        "",
    ).toLowerCase();
    const plan: SubscriptionRow["plan"] =
        planRaw.includes("year") || planRaw.includes("an")
            ? "Anual"
            : "Mensual";

    const sourceRaw = asString(v["source"], "").toLowerCase();
    const source: SubscriptionRow["source"] =
        sourceRaw === "qr"
            ? "qr"
            : sourceRaw === "link"
              ? "link"
              : "organic";

    const email = asString(v["emailMasked"] ?? v["email"], "");

    return {
        id:
            asString(v["subscriptionId"], "") ||
            asString(v["id"], "") ||
            `sub-${i}`,
        emailMasked: v["emailMasked"]
            ? asString(v["emailMasked"])
            : maskEmailValue(email),
        status,
        plan,
        startedAt:
            toIsoDay(v["startedAt"] ?? v["start"] ?? v["createdAt"]) ||
            isoDay(new Date()),
        renewAt:
            toIsoDay(
                v["renewAt"] ?? v["renewsAt"] ?? v["currentPeriodEnd"],
            ) || isoDay(new Date()),
        source,
    };
}

export function coerceUserRow(v: unknown, i: number): UserRow | null {
    if (!isRecord(v)) return null;
    const statusRaw = asString(v["status"], "").toLowerCase();
    const status: UserRow["status"] =
        statusRaw === "active" || statusRaw === "subscribed"
            ? "subscribed"
            : statusRaw === "registered" || statusRaw === "signup"
              ? "registered"
              : "installed";
    const email = asString(v["emailMasked"] ?? v["email"], "");
    return {
        id:
            asString(v["userId"], "") ||
            asString(v["id"], "") ||
            `usr-${i}`,
        emailMasked: v["emailMasked"]
            ? asString(v["emailMasked"])
            : maskEmailValue(email),
        status,
        firstSeen:
            toIsoDay(v["firstSeen"] ?? v["first"] ?? v["createdAt"]) ||
            isoDay(new Date()),
        lastSeen:
            toIsoDay(
                v["lastSeen"] ??
                    v["last"] ??
                    v["updatedAt"] ??
                    v["lastActiveAt"],
            ) || isoDay(new Date()),
    };
}

export function coerceBucketSeries(
    v: unknown,
): Array<{ bucket: string; value: unknown }> {
    if (!Array.isArray(v)) return [];
    return v
        .filter((x) => isRecord(x) && typeof x["bucket"] === "string")
        .map((x) => ({ bucket: String(x["bucket"]), value: x["value"] }));
}

export type ParsedMetrics = {
    installs: number;
    signups: number;
    subscribers: number;
    series: DailyPoint[];
    activity: ActivityItem[];
    subscriptionsList: SubscriptionRow[];
    usersList: UserRow[];
};

export function parseMetrics(res: any): ParsedMetrics {
    // Totals
    const totals = isRecord(res) ? (res["totals"] as any) : null;
    const installs = asNumber(totals?.installs ?? (res as any)?.installs, 0);
    const signups = asNumber(
        totals?.signups ?? totals?.signUps ?? (res as any)?.signups,
        0,
    );
    const subscribers = asNumber(
        totals?.activeSubscribers ??
            totals?.subscribers ??
            (res as any)?.subscribers,
        0,
    );

    // Series: backend shape is an object of arrays: series.installs[], series.newSubscribers[], ...
    let parsedSeries: DailyPoint[] = [];
    const rawSeriesObj = isRecord(res) ? (res["series"] as any) : null;
    if (isRecord(rawSeriesObj)) {
        const installsBuckets = coerceBucketSeries(
            rawSeriesObj["installs"],
        );
        const newSubsBuckets = coerceBucketSeries(
            rawSeriesObj["newSubscribers"],
        );
        const cancelsBuckets = coerceBucketSeries(
            rawSeriesObj["cancellations"],
        );

        const byBucket = new Map<string, DailyPoint>();
        const upsert = (bucket: string) => {
            const key = bucket;
            const existing = byBucket.get(key);
            if (existing) return existing;
            const dateIso = toIsoDay(bucket);
            const p: DailyPoint = {
                date: dateIso || bucket,
                installs: 0,
                newSubs: 0,
                cancels: 0,
                renewals: 0,
            };
            byBucket.set(key, p);
            return p;
        };

        for (const it of installsBuckets) {
            upsert(it.bucket).installs = asNumber(it.value, 0);
        }
        for (const it of newSubsBuckets) {
            upsert(it.bucket).newSubs = asNumber(it.value, 0);
        }
        for (const it of cancelsBuckets) {
            upsert(it.bucket).cancels = asNumber(it.value, 0);
        }

        parsedSeries = Array.from(byBucket.entries())
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([, v]) => v)
            .filter((p) => !!p.date);
    } else {
        // Backward compatibility: if backend returns an array of daily points
        const rawSeries = pickArray(res, [
            "series",
            "daily",
            "points",
            "timeseries",
        ]);
        parsedSeries = rawSeries
            .map(coerceDailyPoint)
            .filter((p): p is DailyPoint => !!p);
    }


    const rawActivity = pickArray(res, ["activity", "events"]);
    const parsedActivity = rawActivity
        .map((v, i) => coerceActivityItem(v, i))
        .filter((p): p is ActivityItem => !!p);

    const rawSubs = pickArray(res, [
        "subscriptions",
        "subs",
        "customers",
        "subscriptionsList",
    ]);
    const parsedSubs = rawSubs
        .map((v, i) => coerceSubscriptionRow(v, i))
        .filter((p): p is SubscriptionRow => !!p);

    const rawUsers = pickArray(res, [
        "users",
        "userList",
        "attributedUsers",
    ]);
    const parsedUsers = rawUsers
        .map((v, i) => coerceUserRow(v, i))
        .filter((p): p is UserRow => !!p);
    return {
        installs,
        signups,
        subscribers,
        series: parsedSeries,
        activity: parsedActivity,
        subscriptionsList: parsedSubs,
        usersList: parsedUsers,
    };
}
